import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { isLoggedIn, clearToken } from '../../lib/auth';
import { getUserInfo, type User } from '../../lib/api/user';
import { useI18n } from '../../hooks/useI18n';
import i18n from '../../i18n';
import { 
  EditSOutline,
  GlobalOutline,
  LockOutline,
  MessageOutline,
  FileOutline,
  HeartOutline,
  DeleteOutline,
  RightOutline,
  SetOutline
} from 'antd-mobile-icons';
import mineBg from '../../assets/images/ai/mineBg.png';
import { I18N_LANGUAGE_OPTIONS, toI18nCode, fromI18nCode, type LanguageOption } from '../../constants/languages';

export default function MySettingPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [showLangChange, setShowLangChange] = useState(false);
  const [language, setLanguage] = useState(() => {
    const currentLang = i18n.language;
    return currentLang === 'en' ? 'English' : '简体中文';
  });
  const [clickCount, setClickCount] = useState(0);
  const [firstClickTime, setFirstClickTime] = useState<number | null>(null);
  const [showDebugOption, setShowDebugOption] = useState(false);
  const [loginType, setLoginType] = useState<string>('');
  const { t } = useI18n();

  // 使用统一的语言选项
  const langList = I18N_LANGUAGE_OPTIONS;

  useEffect(() => {
    if (!isLoggedIn()) {
      navigate('/login');
      return;
    }
    
    // 读取登录类型
    const storedLoginType = localStorage.getItem('loginType') || 'user';
    setLoginType(storedLoginType);
    
    fetchUserInfo();
  }, [navigate]);

  const fetchUserInfo = async () => {
    try {
      const response = await getUserInfo();
      console.log('用户信息响应:', response);
      
      if (response.success && response.data) {
        setUser(response.data);
      } else {
        console.error('获取用户信息失败:', response.errorMessage);
        handleLogout();
      }
    } catch (error: any) {
      console.error('获取用户信息错误:', error);
      handleLogout();
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    clearToken();
    // 清除登录类型
    localStorage.removeItem('loginType');
    navigate('/login');
  };

  const handleLanguageClick = () => {
    setShowLangChange(true);
  };

  const handleAboutUs = () => {
    // 跳转到关于我们页面
    console.log('跳转到关于我们');
  };

  const onCancel = () => {
    setShowLangChange(false);
  };

  const onSelectLang = async (lang: LanguageOption) => {
    try {
      await i18n.changeLanguage(lang.value);
      setLanguage(lang.text);
      setShowLangChange(false);
      // 强制重新渲染页面以应用新语言
      window.location.reload();
    } catch (error) {
      console.error(t('settings.languageSwitchFailed', '切换语言失败'), error);
    }
  };

  const handleAvatarClick = () => {
    const currentTime = Date.now();
    
    if (firstClickTime === null) {
      // 第一次点击
      setFirstClickTime(currentTime);
      setClickCount(1);
    } else {
      const timeDiff = currentTime - firstClickTime;
      
      if (timeDiff <= 3000) {
        // 在3秒内
        const newClickCount = clickCount + 1;
        setClickCount(newClickCount);
        
        if (newClickCount >= 10) {
          // 达到10次点击，显示调试选项
          console.log('🔧 调试模式已激活，显示调试选项');
          setShowDebugOption(true);
          
          // 重置计数
          setClickCount(0);
          setFirstClickTime(null);
        }
      } else {
        // 超过3秒，重新开始计数
        setFirstClickTime(currentTime);
        setClickCount(1);
      }
    }
  };

  // 获取用户头像显示内容
  const getAvatarContent = () => {
    if (user?.avatar) {
      return (
        <img 
          src={user.avatar} 
          alt="avatar" 
          className="w-15 h-15 rounded-full border-1 border-[#888dfa]"
        />
      );
    } else if (user?.username) {
      const displayName = user.full_name || user.username;
      const avatarText = displayName.length > 2 
        ? displayName.substr(displayName.length - 2) 
        : displayName;
      
      return (
        <div 
          className="w-15 h-15 rounded-full flex items-center justify-center text-white text-3.5"
          style={{ background: '#888dfa' }}
        >
          {avatarText}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F7FA] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
          <p className="text-gray-500 mt-4">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div 
      className="min-h-screen bg-[#F5F7FA] bg-no-repeat bg-cover bg-center flex justify-center"
      style={{
        backgroundImage: `url(${mineBg})`
      }}
    >
      {/* PC端适配容器 - 原生网页端设计，无边界感 */}
      <motion.div 
        className="w-full min-h-screen text-3 overflow-x-hidden box-content text-left relative"
        style={{
          fontSize: '12px', // 24px / 2 = 12px
          maxWidth: '480px' // 限制PC端最大宽度为480px，保持移动端体验
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
      {/* 用户信息区域 */}
      <motion.div 
        className="flex items-center mx-4 mt-7.5 mb-6.5"
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.2, duration: 0.6 }}
      >
        <div className="w-15 h-15 cursor-pointer" onClick={handleAvatarClick}>
          {getAvatarContent()}
        </div>
        <div className="ml-3">
          <div className="text-black text-5 leading-7.5 font-medium">
            {user.full_name || user.username}
          </div>
          {
            user.email && (
              <div className="text-3.5 leading-5.5 font-normal text-[#909399]">
                {user.email}
              </div>
            )
          }
        </div>
      </motion.div>

      {/* 设置选项卡片 */}
      <motion.div 
        className="mx-3 mt-2.5 bg-white/75 rounded-2.5 shadow-[0px_1px_3.5px_0px_rgba(224,232,255,0.65)]"
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.4, duration: 0.6 }}
      >
        <div className="pt-3.75 pb-0 pl-4 font-medium text-4">
          {t('settings.title', '设置')}
        </div>
        
        {/* 我的资料 */}
        <motion.div 
          className="h-13 mx-4 flex items-center justify-between cursor-pointer"
          onClick={() => navigate('/settings/my-profile')}
          whileHover={{ backgroundColor: 'rgba(0,0,0,0.02)' }}
          whileTap={{ scale: 0.98 }}
        >
          <div className="flex items-center">
            <div className="mr-2">
              <EditSOutline className="w-7.5 h-7.5 text-blue-600" />
            </div>
            <div className="text-[#303133] text-3.5">
              {t('settings.myProfile', '我的资料')}
            </div>
          </div>
          <div className="flex items-center">
            <div className="ml-0.75 h-3.75">
              <RightOutline className="text-4 -ml-0.75 text-[#909399]" />
            </div>
          </div>
        </motion.div>


        {/* 多语言设置 */}
        <motion.div 
          className="h-13 mx-4 flex items-center justify-between cursor-pointer"
          onClick={handleLanguageClick}
          whileHover={{ backgroundColor: 'rgba(0,0,0,0.02)' }}
          whileTap={{ scale: 0.98 }}
        >
          <div className="flex items-center">
            <div className="mr-2">
              <GlobalOutline className="w-7.5 h-7.5 text-green-600" />
            </div>
            <div className="text-[#303133] text-3.5">
              {t('settings.languageSettings', '多语言设置')}
            </div>
          </div>
          <div className="flex items-center">
            <div className="text-3 text-[#909399]">
              {language}
            </div>
            <div className="ml-0.75 h-3.75">
              <RightOutline className="text-4 -ml-0.75 text-[#909399]" />
            </div>
          </div>
        </motion.div>


        {/* 反馈建议 */}
        {/* <motion.div 
          className="h-13 mx-4 flex items-center justify-between cursor-pointer"
          onClick={() => navigate('/settings/feedback')}
          whileHover={{ backgroundColor: 'rgba(0,0,0,0.02)' }}
          whileTap={{ scale: 0.98 }}
        >
          <div className="flex items-center">
            <div className="mr-2">
              <MessageOutline className="w-7.5 h-7.5 text-purple-600" />
            </div>
            <div className="text-[#303133] text-3.5">
              {t('settings.feedback', '反馈建议')}
            </div>
          </div>
          <div className="flex items-center">
            <div className="ml-0.75 h-3.75">
              <RightOutline className="text-4 -ml-0.75 text-[#909399]" />
            </div>
          </div>
        </motion.div> */}

        {/* 服务协议 */}
        {/* <motion.div 
          className="h-13 mx-4 flex items-center justify-between cursor-pointer"
          onClick={() => navigate('/settings/service-agreement')}
          whileHover={{ backgroundColor: 'rgba(0,0,0,0.02)' }}
          whileTap={{ scale: 0.98 }}
        >
          <div className="flex items-center">
            <div className="mr-2">
              <FileOutline className="w-7.5 h-7.5 text-indigo-600" />
            </div>
            <div className="text-[#303133] text-3.5">
              {t('settings.serviceAgreement', '服务协议')}
            </div>
          </div>
          <div className="flex items-center">
            <div className="ml-0.75 h-3.75">
              <RightOutline className="text-4 -ml-0.75 text-[#909399]" />
            </div>
          </div>
        </motion.div> */}

        {/* 隐私政策 */}
        {/* <motion.div 
          className="h-13 mx-4 flex items-center justify-between cursor-pointer"
          onClick={() => navigate('/settings/privacy-policy')}
          whileHover={{ backgroundColor: 'rgba(0,0,0,0.02)' }}
          whileTap={{ scale: 0.98 }}
        >
          <div className="flex items-center">
            <div className="mr-2">
              <FileOutline className="w-7.5 h-7.5 text-teal-600" />
            </div>
            <div className="text-[#303133] text-3.5">
              {t('settings.privacyPolicy', '隐私政策')}
            </div>
          </div>
          <div className="flex items-center">
            <div className="ml-0.75 h-3.75">
              <RightOutline className="text-4 -ml-0.75 text-[#909399]" />
            </div>
          </div>
        </motion.div> */}

        {/* 关于我们 */}
        {/* <motion.div 
          className="h-13 mx-4 flex items-center justify-between cursor-pointer"
          onClick={() => navigate('/settings/about-us')}
          whileHover={{ backgroundColor: 'rgba(0,0,0,0.02)' }}
          whileTap={{ scale: 0.98 }}
        >
          <div className="flex items-center">
            <div className="mr-2">
              <HeartOutline className="w-7.5 h-7.5 text-pink-600" />
            </div>
            <div className="text-[#303133] text-3.5">
              {t('settings.aboutUs', '关于我们')}
            </div>
          </div>
          <div className="flex items-center">
            <div className="ml-0.75 h-3.75">
              <RightOutline className="text-4 -ml-0.75 text-[#909399]" />
            </div>
          </div>
        </motion.div> */}

        {/* 调试选项（仅在激活后显示） */}
        {showDebugOption && (
          <motion.div 
            className="h-13 mx-4 flex items-center justify-between cursor-pointer"
            onClick={() => navigate('/settings/debug-storage')}
            whileHover={{ backgroundColor: 'rgba(255,165,0,0.02)' }}
            whileTap={{ scale: 0.98 }}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="flex items-center">
              <div className="mr-2">
                <SetOutline className="w-7.5 h-7.5 text-orange-600" />
              </div>
              <div className="text-[#303133] text-3.5">
                🔧 调试工具
              </div>
            </div>
            <div className="flex items-center">
              <div className="ml-0.75 h-3.75">
                <RightOutline className="text-4 -ml-0.75 text-[#909399]" />
              </div>
            </div>
          </motion.div>
        )}

        {/* 退出登录 - 仅在普通用户登录时显示 */}
        {loginType === 'user' && (
          <motion.div 
            className="h-13 mx-4 flex items-center justify-between cursor-pointer"
            onClick={handleLogout}
            whileHover={{ backgroundColor: 'rgba(255,0,0,0.02)' }}
            whileTap={{ scale: 0.98 }}
          >
            <div className="flex items-center">
              <div className="mr-2">
                <span className="text-red-600 text-4">🚪</span>
              </div>
              <div className="text-red-600 text-3.5">
                {t('settings.logout', '退出登录')}
              </div>
            </div>
            <div className="flex items-center">
              <div className="ml-0.75 h-3.75">
                <RightOutline className="text-4 -ml-0.75 text-red-400" />
              </div>
            </div>
          </motion.div>
        )}
      </motion.div>

      {/* 底部偏移 */}
      <div className="h-2"></div>

      {/* 语言选择弹窗 */}
      {showLangChange && (
        <motion.div 
          className="fixed inset-0 bg-black/50 flex items-end z-50"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onCancel}
        >
          <motion.div 
            className="w-full bg-white rounded-t-2xl p-4"
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 500 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center text-lg font-medium mb-4">{t('settings.selectLanguage', '选择语言')}</div>
            {langList.map((lang, index) => (
              <motion.div
                key={lang.value}
                className="py-3 px-4 text-center border-b border-gray-100 last:border-b-0 cursor-pointer"
                onClick={() => onSelectLang(lang)}
                whileHover={{ backgroundColor: '#f3f4f6' }}
                whileTap={{ scale: 0.98 }}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                {lang.text}
              </motion.div>
            ))}
            <motion.button
              className="w-full mt-4 py-3 bg-gray-100 rounded-lg text-gray-600"
              onClick={onCancel}
              whileHover={{ backgroundColor: '#e5e7eb' }}
              whileTap={{ scale: 0.98 }}
            >
              {t('common.cancel', '取消')}
            </motion.button>
          </motion.div>
        </motion.div>
      )}
      </motion.div>
    </div>
  );
}